import React from 'react';

// Utils
import {
  getDetailCustomFieldValue,
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';

import CustomExtendedDataSection from '../../components/CustomExtendedDataSection/CustomExtendedDataSection.js';

/**
 * Renders custom listing fields.
 * - SectionDetails is used if schemaType is 'enum', 'long', or 'boolean'
 * - SectionMultiEnum is used if schemaType is 'multi-enum'
 * - SectionText is used if schemaType is 'text'
 *
 * @param {*} props include publicData, metadata, listingFieldConfigs, categoryConfiguration
 * @returns React.Fragment containing aforementioned components
 */
const CustomListingFields = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration, intl } = props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  // Fields are shown by default. Set showConfig.displayOnListingPage to false to explicitly hide a field.
  const displayableFieldConfigs = listingFieldConfigs.filter(
    fieldConfig => fieldConfig.showConfig?.displayOnListingPage !== false
  );

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };
  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      displayableFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  const isFreelancer = publicData?.listingType === 'freelancers';
  const isJob = publicData?.listingType === 'jobs';

  // Inject follower counts into the social_platforms field props
  const enrichedPropsForCustomFields = propsForCustomFields.map(fieldProps => {
    if (fieldProps.key === 'social_platforms' && isFreelancer) {
      const followerCounts = Object.keys(publicData).reduce((acc, key) => {
        if (key.startsWith('followers_')) {
          acc[key] = publicData[key];
        }
        return acc;
      }, {});
      return { ...fieldProps, followerCounts };
    }
    return fieldProps;
  });

  // Inject category/subcategory and, for jobs, language + revisions_included as detail entries
  const enrichedPropsWithSubcategory = (() => {
    const level1Value = publicData[`${categoryPrefix}1`];
    const level2Value = publicData[`${categoryPrefix}2`];
    const level1Config = listingCategoriesConfig.find(cat => cat.id === level1Value);

    const injected = [];

    // Main category (jobs only)
    if (isJob && level1Config?.name) {
      injected.push({
        key: 'category',
        schemaType: 'subcategoryDetail',
        label: intl.formatMessage({ id: 'CustomListingFields.categoryLabel' }),
        value: level1Config.name,
      });
    }

    // Subcategory (all types)
    if (level2Value) {
      const subcatConfig = level1Config?.subcategories?.find(cat => cat.id === level2Value);
      injected.push({
        key: 'subcategory',
        schemaType: 'subcategoryDetail',
        label: intl.formatMessage({ id: 'CustomListingFields.subcategoryLabel' }),
        value: subcatConfig?.name || level2Value,
      });
    }

    // Language + Revisions included (jobs only — pulled out of SectionDetails for ordering)
    if (isJob) {
      const languageConfig = listingFieldConfigs.find(f => f.key === 'language');
      const languageValue = publicData.language;
      if (languageConfig && languageValue) {
        const langOption = languageConfig.enumOptions?.find(o => o.option === languageValue);
        injected.push({
          key: 'language',
          schemaType: 'subcategoryDetail',
          label: languageConfig.showConfig?.label || 'Language',
          value: langOption?.label || languageValue,
        });
      }

      const revisionsConfig = listingFieldConfigs.find(f => f.key === 'revisions_included');
      const revisionsValue = publicData.revisions_included;
      if (revisionsConfig && revisionsValue != null) {
        injected.push({
          key: 'revisions_included',
          schemaType: 'subcategoryDetail',
          label: revisionsConfig.showConfig?.label || 'Revisions included',
          value: String(revisionsValue),
        });
      }
    }

    if (injected.length === 0) return enrichedPropsForCustomFields;
    return [...injected, ...enrichedPropsForCustomFields];
  })();

  const sectionDetailsProps = {
    ...props,
    isFieldForCategory: isFieldForSelectedCategories,
    fieldConfigs: displayableFieldConfigs,
    heading: 'ListingPage.detailsTitle',
  };

  const pickExtendedDataFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, showConfig = {} } = config;
    const listingType = publicData.listingType;
    const isTargetListingType = isFieldForListingType(listingType, config);
    const isTargetCategory = isFieldForCategory(currentCategories, config);

    const { isDetail, label } = showConfig;
    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = publicDataValue != null ? publicDataValue : metadataValue;

    if (isDetail && isTargetListingType && isTargetCategory && typeof value !== 'undefined') {
      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        value,
        schemaType,
        key,
        label,
        intl,
        'ListingPage'
      );

      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }
    return filteredConfigs;
  };

  return (
    <CustomExtendedDataSection
      sectionDetailsProps={sectionDetailsProps}
      propsForCustomFields={enrichedPropsWithSubcategory}
      idPrefix="listingPage"
      pickExtendedDataFields={pickExtendedDataFields}
      isFreelancer={isFreelancer}
      isJob={isJob}
    />
  );
};

export default CustomListingFields;
